import { useCallback, useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import BusinessEditDialog from "@/components/BusinessEditDialog";
import { getProfilePhotoUrl, optimizeImage } from "@/lib/mediaUrls";

const CategoryPill = ({ active, onClick, children, testId }) => (
  <button
    onClick={onClick}
    className={`text-xs px-3 py-1 rounded-full border transition-colors whitespace-nowrap ${
      active
        ? "bg-purple-600/40 border-purple-400 text-white"
        : "bg-black/30 border-gray-700 text-gray-300 hover:border-purple-500/60"
    }`}
    data-testid={testId}
  >
    {children}
  </button>
);

const BusinessCard = ({ b }) => (
  <Link
    to={`/business/${b.business_id}`}
    className="group"
    data-testid={`business-card-${b.business_id}`}
  >
    <Card className="overflow-hidden bg-gradient-to-br from-gray-900/80 to-black border-purple-500/20 hover:border-purple-500/60 transition-all h-full flex flex-col">
      <div className="aspect-[16/9] relative overflow-hidden bg-gradient-to-br from-purple-900/40 to-pink-900/40">
        {b.cover_image ? (
          <img
            src={optimizeImage(b.cover_image, 'card')}
            alt={b.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-5xl">💼</div>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <h3 className="text-white font-bold truncate">{b.name}</h3>
        <p className="text-purple-300 text-xs">{b.category?.replace("_", " ")}</p>
        <p className="text-gray-400 text-sm line-clamp-2">{b.description || <span className="italic text-gray-600">No description yet.</span>}</p>
        <div className="mt-auto flex items-center justify-between text-xs text-gray-500 pt-2">
          <div className="flex items-center gap-2 min-w-0">
            <img
              src={getProfilePhotoUrl(b.owner || {})}
              alt={b.owner?.artist_name}
              className="w-5 h-5 rounded-full object-cover"
            />
            <span className="truncate">{b.owner?.artist_name}</span>
          </div>
          <span>🛠 {b.service_count || 0}</span>
        </div>
      </div>
    </Card>
  </Link>
);

const BusinessHubPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(null));
    api.get("/business/categories").then(r => setCategories(r.data?.categories || [])).catch(() => {});
  }, []);

  // Ref so `search` keystrokes don't force fetchBusinesses re-creation
  // (which would refetch on every letter typed). fetchBusinesses reads
  // the latest search value via ref; the effect only refetches on
  // category change; the form submit path calls fetchBusinesses directly.
  const searchRef = useRef(search);
  useEffect(() => { searchRef.current = search; }, [search]);

  const fetchBusinesses = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (activeCat) params.category = activeCat;
      const s = searchRef.current;
      if (s && s.trim()) params.q = s.trim();
      const r = await api.get("/businesses", { params });
      setBusinesses(r.data?.businesses || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load businesses");
    } finally {
      setLoading(false);
    }
  }, [activeCat]);

  useEffect(() => { fetchBusinesses(); }, [fetchBusinesses]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    fetchBusinesses();
  };

  const onCreated = (biz) => {
    setShowEditor(false);
    toast.success(`"${biz.name}" created!`);
    navigate(`/business/${biz.business_id}`);
  };

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="business-hub-page">
      <SharedNavigation currentUser={user} activePage="business" />
      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white flex items-center gap-3">
              <span>💼</span> Business Hub
            </h1>
            <p className="text-gray-400 mt-1 text-sm">
              Discover and book independent creators. List your own business and grow.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/services">
              <Button variant="outline" className="text-white border-purple-500/50" data-testid="business-services-link">
                Browse Services
              </Button>
            </Link>
            {user && (
              <Button
                onClick={() => setShowEditor(true)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold"
                data-testid="business-create-button"
              >
                + List Your Business
              </Button>
            )}
          </div>
        </div>

        {/* Search + filters */}
        <form onSubmit={onSearchSubmit} className="mb-4 flex gap-2 flex-wrap">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search businesses…"
            className="bg-black/30 text-white border-gray-700 max-w-sm"
            data-testid="business-search-input"
          />
          <Button type="submit" className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="business-search-submit">
            Search
          </Button>
        </form>
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1" data-testid="business-category-filters">
          <CategoryPill active={!activeCat} onClick={() => setActiveCat("")} testId="cat-all">All</CategoryPill>
          {categories.map((c) => (
            <CategoryPill
              key={c.key}
              active={activeCat === c.key}
              onClick={() => setActiveCat(c.key)}
              testId={`cat-${c.key}`}
            >
              {c.label}
            </CategoryPill>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20" data-testid="business-loading">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
          </div>
        ) : businesses.length === 0 ? (
          <div className="text-center py-12" data-testid="business-empty">
            <p className="text-5xl mb-3">💼</p>
            <p className="text-white text-lg font-semibold">No businesses found</p>
            <p className="text-gray-400 text-sm mt-1">
              {activeCat ? "Try a different category." : "Be the first to list yours."}
            </p>
            {user && !activeCat && (
              <Button
                onClick={() => setShowEditor(true)}
                className="mt-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white"
                data-testid="business-empty-create"
              >
                + List Your Business
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="business-grid">
            {businesses.map((b) => <BusinessCard key={b.business_id} b={b} />)}
          </div>
        )}
      </div>

      <BusinessEditDialog
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSaved={onCreated}
        categories={categories}
      />
    </div>
  );
};

export default BusinessHubPage;
